import {
    Box,
    Collapse,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    Button,
    Typography,
    Divider,
    CardContent,
    TablePagination,
} from "@mui/material";
import { AppCard } from '../../components/common/AppCard';
import { RecordContextProvider } from "react-admin";
import { useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";

// Material UI Icons
import InventoryIcon from '@mui/icons-material/Inventory2';
import PriceChangeIcon from "@mui/icons-material/PriceChangeOutlined";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import PieChartIcon from "@mui/icons-material/PieChart";
import ClassIcon from "@mui/icons-material/Class";
import GetAppIcon from '@mui/icons-material/GetApp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { AccountPortfolio, capitalizeFirst, universalAddressToSymbol } from "../../types";
import { AnimatedValue } from "../../components/AnimatedValue";
import { resolveValuation } from '../../utils/resolveValuation';

interface Props {
    portfolio: AccountPortfolio | null;
    currency: "eur" | "usd" | "chf";
    liveTrigger?: unknown;
    perAccountSnapshots?: AccountPortfolio[];
}

export const PortfolioAssets = ({ portfolio, currency, liveTrigger, perAccountSnapshots }: Props) => {
    const navigate = useNavigate();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [csvLoading, setCsvLoading] = useState(false);
    const [expandedSymbols, setExpandedSymbols] = useState<Set<string>>(new Set());

    const isCombined = !!perAccountSnapshots && perAccountSnapshots.length > 0;

    // Reset page and expanded state when account/data source changes
    useEffect(() => {
        setPage(0);
        setExpandedSymbols(new Set());
    }, [portfolio?.account_id, isCombined]);

    // Merge balances across accounts by symbol for combined view
    // Must be before early return to keep hook count stable
    const mergedAssetEntries = useMemo(() => {
        if (!isCombined || !perAccountSnapshots) return null;

        const bySymbol = new Map<string, {
            symbol: string;
            address: string;
            totalAmount: number;
            totalValue: Record<string, number>;
            totalShare: number;
            perAccount: { accountId: string; amount: number; value: number; share: number }[];
        }>();

        for (const snapshot of perAccountSnapshots) {
            if (!snapshot.asset_data?.balances) continue;

            for (const [assetId, balance] of Object.entries(snapshot.asset_data.balances)) {
                const key = balance.symbol?.toUpperCase() || assetId;
                const existing = bySymbol.get(key) || {
                    symbol: key,
                    address: balance.address,
                    totalAmount: 0,
                    totalValue: {},
                    totalShare: 0,
                    perAccount: [],
                };

                existing.totalAmount += balance.amount || 0;
                const balVal: Record<string, number> = {};
                for (const cur of ['eur', 'usd', 'chf']) {
                    const v = resolveValuation(balance.valuation, cur);
                    if (v != null) balVal[cur] = v;
                }
                for (const [cur, val] of Object.entries(balVal)) {
                    existing.totalValue[cur] = (existing.totalValue[cur] || 0) + val;
                }
                const share = snapshot.asset_data.distribution?.[assetId] || 0;
                existing.perAccount.push({
                    accountId: snapshot.account_id,
                    amount: balance.amount || 0,
                    value: resolveValuation(balance.valuation, currency) ?? 0,
                    share,
                });

                bySymbol.set(key, existing);
            }
        }

        // Calculate combined portfolio total for share percentages
        const totalPortfolioValue = perAccountSnapshots.reduce(
            (sum, s) => sum + (resolveValuation(s.valuation, currency) ?? 0), 0
        );

        return Array.from(bySymbol.values())
            .map((entry) => ({
                ...entry,
                totalShare: totalPortfolioValue > 0
                    ? (entry.totalValue[currency] || 0) / totalPortfolioValue
                    : 0,
            }))
            .sort((a, b) => b.totalShare - a.totalShare);
    }, [perAccountSnapshots, currency, isCombined]);

    if (!portfolio?.asset_data?.balances) {
        return <Typography>No asset data available.</Typography>;
    }

    const generateCsv = () => {
        if (!portfolio?.asset_data?.balances) return;

        setCsvLoading(true);

        const assetEntries = Object.entries(portfolio.asset_data.balances)
            .sort(([, aBalance], [, bBalance]) => {
                const aShare = portfolio.asset_data.distribution?.[aBalance.address] || 0;
                const bShare = portfolio.asset_data.distribution?.[bBalance.address] || 0;
                return bShare - aShare;
            });

        const headers = ['asset', 'address', 'amount', `value-${currency}`, 'share'];

        const rows = assetEntries.map(([assetId, assetBalance]) => {
            const portfolioShare = portfolio.asset_data.distribution?.[assetId] || 0;
            const value = assetBalance.valuation?.global?.[currency.toLowerCase()] || 0;

            return [
                assetBalance.symbol.toUpperCase() || assetId,
                assetBalance.address,
                assetBalance.amount?.toFixed(8) || 'N/A',
                value.toFixed(2),
                (portfolioShare * 100).toFixed(2)
            ];
        });

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${cell}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `portfolio-${portfolio.account_id}-assets-${portfolio.timestamp.toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setCsvLoading(false);
    };

    const assetEntries = Object.entries(portfolio.asset_data.balances)
        .sort(([, aBalance], [, bBalance]) => {
            const aShare = portfolio.asset_data.distribution?.[aBalance.address] || 0;
            const bShare = portfolio.asset_data.distribution?.[bBalance.address] || 0;
            return bShare - aShare; // descending
        });

    const toggleExpand = (symbol: string) => {
        setExpandedSymbols((prev) => {
            const next = new Set(prev);
            if (next.has(symbol)) next.delete(symbol);
            else next.add(symbol);
            return next;
        });
    };

    const paginatedAssets = assetEntries.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const openAssetLink = (asset: string) => {
        const [location, address] = asset.split(':');

        if (location === "ethereum") {
            if (address === "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE") {
                window.open("https://etherscan.io/chart/etherprice", "_blank");
            } else {
                window.open(`https://etherscan.io/token/${address}`, "_blank");
            }
        } else if (location === "binance") {
            const stablecoins = ['usdc', 'usdt', 'busd', 'dai', 'tusd'];
            const symbol = address.toLowerCase();
            const pair = stablecoins.includes(symbol)
                ? `${address.toUpperCase()}_USD`
                : `${address.toUpperCase()}_USDC`;
            window.open(`https://www.binance.com/en/trade/${pair}`, "_blank");
        } else if (location === "kraken") {
            window.open(`https://pro.kraken.com/app/trade/${address.toLowerCase()}-eur`, "_blank");
        } else {
            console.warn(`No handler for location: '${location}'`);
        }
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InventoryIcon fontSize="small" />
                    Assets
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<GetAppIcon />}
                    disabled={csvLoading || assetEntries.length === 0}
                    size="small"
                    onClick={generateCsv}
                    sx={{ width: 120 }}
                >
                    CSV
                </Button>
            </Box>
            <AppCard>
                <CardContent sx={{ paddingX: 0, paddingTop: 0, "&:last-child": { paddingBottom: 0 } }}>

                    {/* Column Labels */}
                    <Grid container alignItems="center" spacing={0} sx={{ paddingX: 2, paddingY: 1, backgroundColor: "action.hover" }}>
                        <Grid item xs={5}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <ClassIcon fontSize="small" /> Asset
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <PriceChangeIcon fontSize="small" /> Amount
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <LocalAtmIcon fontSize="small" /> Value
                            </Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: "right" }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                                <PieChartIcon fontSize="small" />
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Asset Data */}
                    {isCombined && mergedAssetEntries ? (
                        // Combined view: merged rows with expand/collapse
                        <List dense sx={{ padding: 0, margin: 0 }}>
                            {mergedAssetEntries
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((entry, index) => {
                                    const isExpanded = expandedSymbols.has(entry.symbol);
                                    return (
                                        <Box key={entry.symbol}>
                                            <ListItem disableGutters sx={{ padding: 0, margin: 0 }}>
                                                <ListItemButton
                                                    sx={{
                                                        flexDirection: "column",
                                                        alignItems: "stretch",
                                                        paddingY: 0.75,
                                                        "&:hover": { backgroundColor: "action.hover" },
                                                    }}
                                                    onClick={() => toggleExpand(entry.symbol)}
                                                >
                                                    <Grid container alignItems="center" spacing={0} sx={{ width: "100%", padding: 0, margin: 0 }}>
                                                        <Grid item xs={5}>
                                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                                {entry.perAccount.length > 1 && (
                                                                    <IconButton size="small" sx={{ p: 0 }}>
                                                                        {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                                                    </IconButton>
                                                                )}
                                                                <Box>
                                                                    <Typography variant="body1" color="textPrimary" sx={{ fontWeight: "bold" }}>
                                                                        {entry.symbol}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        {entry.perAccount.length} account{entry.perAccount.length > 1 ? 's' : ''}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <Typography variant="body1" color="textPrimary">
                                                                {entry.totalAmount.toFixed(8)}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={2}>
                                                            <Typography variant="body1" color="textPrimary">
                                                                {(entry.totalValue[currency] || 0).toLocaleString('de-DE', { maximumFractionDigits: 2 })} {currency.toUpperCase()}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={2} sx={{ textAlign: "right" }}>
                                                            <Typography variant="body2" color="textPrimary">
                                                                {(entry.totalShare * 100).toFixed(2)}%
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </ListItemButton>
                                            </ListItem>

                                            {/* Per-account breakdown */}
                                            {entry.perAccount.length > 1 && (
                                                <Collapse in={isExpanded}>
                                                    {entry.perAccount.map((pa) => (
                                                        <Box key={pa.accountId} sx={{ pl: 6, pr: 2, py: 0.5, backgroundColor: "action.hover" }}>
                                                            <Grid container alignItems="center" spacing={0}>
                                                                <Grid item xs={5}>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        {pa.accountId}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={3}>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        {pa.amount.toFixed(8)}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={2}>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        {pa.value.toLocaleString('de-DE', { maximumFractionDigits: 2 })} {currency.toUpperCase()}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={2} sx={{ textAlign: "right" }}>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        —
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    ))}
                                                </Collapse>
                                            )}
                                            {index < mergedAssetEntries.length - 1 && <Divider />}
                                        </Box>
                                    );
                                })}
                        </List>
                    ) : (
                        // Single-account rendering
                        assetEntries.length === 0 ? (
                            <Box sx={{ p: 2 }}>
                                <Typography variant="body2" color="textSecondary">
                                    No assets available.
                                </Typography>
                            </Box>
                        ) : (
                            <List dense sx={{ padding: 0, margin: 0 }}>
                                {paginatedAssets.map(([assetId, assetBalance], index) => {
                                    const portfolioShare = portfolio.asset_data.distribution?.[assetId] || 0;

                                    return (
                                        <RecordContextProvider key={assetId} value={{ id: assetId, ...assetBalance }}>
                                            <ListItem disableGutters sx={{ padding: 0, margin: 0 }}>
                                                <ListItemButton
                                                    sx={{
                                                        flexDirection: "column",
                                                        alignItems: "stretch",
                                                        paddingY: 0.75,
                                                        "&:hover": { backgroundColor: "action.hover" },
                                                    }}
                                                    onClick={() => openAssetLink(assetBalance.address)}
                                                >
                                                    {/* Asset Details Grid */}
                                                    <Grid container alignItems="center" spacing={0} sx={{ width: "100%", padding: 0, margin: 0 }}>

                                                        {/* Symbol */}
                                                        <Grid item xs={5}>
                                                            <Typography variant="body1" color="textPrimary" sx={{ fontWeight: "bold" }}>
                                                                {assetBalance.symbol
                                                                    ? assetBalance.symbol.toUpperCase()
                                                                    : universalAddressToSymbol(assetBalance.address) ?? assetId}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {capitalizeFirst(assetBalance.address) || "N/A"}
                                                            </Typography>
                                                        </Grid>

                                                        {/* Amount */}
                                                        <Grid item xs={3}>
                                                            <Typography variant="body1" color="textPrimary">
                                                                {assetBalance.amount ? assetBalance.amount.toFixed(8) : "N/A"}
                                                            </Typography>
                                                        </Grid>

                                                        {/* Value */}
                                                        <Grid item xs={2}>
                                                            {(() => {
                                                                const v = resolveValuation(assetBalance.valuation, currency);
                                                                return v != null ? (
                                                                    <AnimatedValue
                                                                        value={v}
                                                                        trigger={liveTrigger}
                                                                        format={(v) => `${v.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${currency.toUpperCase()}`}
                                                                        variant="body1"
                                                                    />
                                                                ) : (
                                                                    <Typography variant="body1" color="textPrimary">N/A</Typography>
                                                                );
                                                            })()}
                                                        </Grid>

                                                        {/* Portfolio Share */}
                                                        <Grid item xs={2} sx={{ textAlign: "right" }}>
                                                            <Typography variant="body2" color="textPrimary">
                                                                {(portfolioShare * 100).toFixed(2)}%
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </ListItemButton>
                                            </ListItem>
                                            {index < assetEntries.length - 1 && <Divider />}
                                        </RecordContextProvider>
                                    );
                                })}
                            </List>
                        )
                    )}
                    {((isCombined ? mergedAssetEntries?.length : assetEntries.length) || 0) > rowsPerPage && (
                        <TablePagination
                            component="div"
                            count={(isCombined ? mergedAssetEntries?.length : assetEntries.length) || 0}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                    )}
                </CardContent>
            </AppCard>
        </>
    );
};